import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import data from '../data.json';
import { computeEventAffordances } from '../lib/eventEngine';
import { resolveEventToLensDelta } from '../lib/eventResolutionEngine';

const LensContext = createContext();

export const useLens = () => useContext(LensContext);

const parseUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('B')) return null;
  return {
    B: parseFloat(params.get('B')) || 0.7,
    S: parseFloat(params.get('S')) || 0.6,
    N: parseFloat(params.get('N')) || 0.3,
    I: {
      openness: parseFloat(params.get('o')) || 0.7,
      fear: parseFloat(params.get('f')) || 0.2,
      care: parseFloat(params.get('c')) || 0.6,
      control: parseFloat(params.get('k')) || 0.2,
    },
    P: params.get('P') || data.projectionModes[0],
  };
};

export const LensProvider = ({ children }) => {
  const urlState = parseUrlParams();
  const [B, setB] = useState(urlState?.B ?? 0.7);
  const [S, setS] = useState(urlState?.S ?? 0.6);
  const [N, setN] = useState(urlState?.N ?? 0.3);
  const [I, setI] = useState(urlState?.I ?? {
    openness: 0.7,
    fear: 0.2,
    care: 0.6,
    control: 0.2,
  });
  const [P, setP] = useState(urlState?.P ?? data.projectionModes[0]);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [residue, setResidue] = useState(0);
  const [experienceHistory, setExperienceHistory] = useState([]);
  const [coherenceMemory, setCoherenceMemory] = useState({
    body: 0,
    sensory: 0,
    emotional: 0,
    meaning: 0,
    relational: 0,
    control: 0,
  });

  const capacityVector = useMemo(() => {
    const { openness, fear, care, control } = I;
    const axes = {
      body: B,
      sensory: S,
      emotional: (openness - fear + 1) / 2, // Normalize from [-1, 1] to [0, 1]
      meaning: 1 - N,
      relational: care,
      control: 1 - control,
    };
    return data.axis_definitions.map(axis => ({
      ...axis,
      value: Math.max(0, Math.min(1, axes[axis.id])), // Clamp between 0 and 1
    }));
  }, [B, S, N, I]);

  const gradientVector = useMemo(() => {
    const IDEAL = 1.0;
    return capacityVector.map(axis => ({
      ...axis,
      gradient: Math.max(0, IDEAL - axis.value),
    }));
  }, [capacityVector]);

  const capacity = useMemo(() => {
    if (capacityVector.length === 0) return 0;
    return capacityVector.reduce((sum, axis) => sum + axis.value, 0) / capacityVector.length;
  }, [capacityVector]);

  const alignment = useMemo(() => {
    const { openness, care, fear, control } = I;
    let alignmentValue = ((openness + care) - (fear + control)) / 2;
    alignmentValue = (alignmentValue + 1) / 2;
    alignmentValue *= (1 - N);
    return alignmentValue;
  }, [I, N]);

  const pressure = useMemo(() => {
    const FLOW = 1.0;
    return Math.max(0, FLOW - (capacity * alignment));
  }, [capacity, alignment]);

  const integrationCapacity = useMemo(() => {
    return (1 - N) * I.openness * B;
  }, [N, I.openness, B]);

  const phenomenology = useMemo(() => {
    if (pressure < 0.2) return data.phenomenology.clarity;
    if (pressure < 0.5) return data.phenomenology.intensity;
    if (pressure < 0.8) return data.phenomenology.overwhelm;
    return data.phenomenology.dissolution;
  }, [pressure]);

  const explanation = useMemo(() => {
    const highestGradientAxis = gradientVector.reduce((max, axis) => axis.gradient > max.gradient ? axis : max, gradientVector[0] || {label: 'N/A'});

    // Use projection-mode-specific description if available
    const modeDescription = phenomenology.descriptions ? phenomenology.descriptions[P] || phenomenology.descriptions.abstract : phenomenology.description;

    const resistanceNote = highestGradientAxis.gradient > 0.3
      ? ` The greatest resistance is in the ${highestGradientAxis.label.toLowerCase()} dimension.`
      : '';

    return `${modeDescription}${resistanceNote}`;
  }, [phenomenology, gradientVector, P]);

  const polygonVoice = useMemo(() => {
    if (capacityVector.length === 0) return '';
    const sorted = [...capacityVector].sort((a, b) => b.value - a.value);
    const strongest = sorted[0];
    const weakest = sorted[sorted.length - 1];
    const balance = Math.max(...capacityVector.map(a => a.value)) - Math.min(...capacityVector.map(a => a.value));

    if (pressure < 0.2) {
      return balance < 0.3
        ? 'Your aperture is wide and balanced — like a clear window.'
        : `Open overall, but strongest in ${strongest.label.toLowerCase()} and narrowest in ${weakest.label.toLowerCase()}.`;
    }
    if (pressure < 0.5) {
      return `The aperture is narrowing around ${weakest.label.toLowerCase()}. Intensity is building there, like light concentrating through a smaller opening.`;
    }
    if (pressure < 0.8) {
      return `Significant constriction in ${weakest.label.toLowerCase()}. The same field is pushing through a smaller space — pressure is felt most there.`;
    }
    return `The aperture is straining. ${weakest.label} is nearly closed. What was flowing freely is now forced through a very narrow channel.`;
  }, [capacityVector, pressure]);

  const shareUrl = useMemo(() => {
    const params = new URLSearchParams({
      B: B.toFixed(2), S: S.toFixed(2), N: N.toFixed(2),
      o: I.openness.toFixed(2), f: I.fear.toFixed(2),
      c: I.care.toFixed(2), k: I.control.toFixed(2),
      P,
    });
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }, [B, S, N, I, P]);

  const surfacedEvents = useMemo(() => {
    return computeEventAffordances(gradientVector, pressure, P, coherenceMemory);
  }, [gradientVector, pressure, P, coherenceMemory]);

  useEffect(() => {
    const DECAY = 0.85;
    const MAX_HISTORY = 100;
    const INTERVAL_MS = 1000;

    const interval = setInterval(() => {
      setResidue(prevResidue => {
        const newResidue = prevResidue * DECAY + pressure * (1 - integrationCapacity);

        const dominantDomains = gradientVector
          .sort((a, b) => b.gradient - a.gradient)
          .slice(0, 2)
          .map(axis => axis.id);

        setExperienceHistory(prevHistory => [
          ...prevHistory.slice(-MAX_HISTORY + 1),
          {
            t: Date.now(),
            pressure,
            domains: dominantDomains,
            residue: newResidue
          }
        ]);

        return newResidue;
      });

      setCoherenceMemory(prevCM => {
        const newCM = { ...prevCM };
        const totalGradient = gradientVector.reduce((sum, axis) => sum + axis.gradient, 0);

        if (totalGradient > 0) {
          gradientVector.forEach(axis => {
            const domainWeight = axis.gradient / totalGradient;
            const domainPressure = pressure * domainWeight;
            const updatedValue = prevCM[axis.id] * DECAY + domainPressure * (1 - integrationCapacity);
            newCM[axis.id] = Math.max(0, Math.min(1, updatedValue)); // Clamp to [0, 1]
          });
        }

        return newCM;
      });

    }, INTERVAL_MS);

    return () => clearInterval(interval);
  }, [pressure, integrationCapacity, gradientVector]);

  const resolveEvent = (event) => {
    const lensState = { B, S, N, I };
    const { lensDelta, residueDelta } = resolveEventToLensDelta(event, lensState, integrationCapacity, pressure);

    // Apply lens deltas
    if (lensDelta.B !== undefined) setB(lensDelta.B);
    if (lensDelta.S !== undefined) setS(lensDelta.S);
    if (lensDelta.N !== undefined) setN(lensDelta.N);
    if (lensDelta.I !== undefined) setI(lensDelta.I);

    // Correctly calculate and update residue
    const newResidue = residue + residueDelta;
    setResidue(newResidue);

    // Append to experienceHistory
    setExperienceHistory(prevHistory => [
      ...prevHistory,
      {
        t: Date.now(),
        pressure,
        residue: newResidue,
        event,
      },
    ]);
  };

  const value = {
    B, setB,
    S, setS,
    N, setN,
    I, setI,
    P, setP,
    capacity,
    alignment,
    pressure,
    phenomenology,
    explanation,
    projectionModes: data.projectionModes,
    capacityVector,
    gradientVector,
    axis_definitions: data.axis_definitions,
    axis_descriptions: data.axis_descriptions,
    support_affordances: data.support_affordances,
    surfacedEvents,
    hoveredEvent, setHoveredEvent,
    residue,
    experienceHistory,
    integrationCapacity,
    coherenceMemory,
    resolveEvent,
    polygonVoice,
    shareUrl,
  };

  return (
    <LensContext.Provider value={value}>
      {children}
    </LensContext.Provider>
  );
};
